import {
  FluidButtonType,
  FluidConfirmationDialogTranslationOptions,
  FluidModalAction,
  FluidSize,
  FluidTheme,
  FluidThemeInterface,
} from '@lmig/fluid-core';
import {
  Component,
  Element,
  Event,
  EventEmitter,
  h,
  Method,
  Prop,
  State,
  Watch,
} from '@stencil/core';

import { FluidModalCustomEvent } from '../../components';
import { ATTR } from '../../constants/constants';
import { useI18n } from '../../providers/fluid-i18n-provider/adapter/fluid-i18n.adapter';
import { FluidI18nStore } from '../../providers/fluid-i18n-provider/store/fluid-i18n.store';
import { Utils } from '../../utils/utils';

/**
 * @displayName Confirmation Dialog
 *
 */
@Component({
  tag: 'fluid-confirmation-dialog',
  shadow: true,
})
export class FluidConfirmationDialog implements FluidThemeInterface {
  // ================================================================ //
  // -- Own Properties (Private)

  @State() _modalActions: FluidModalAction[]; // TODO: this should be private (breaks unit tests though)

  // ================================================================ //
  // -- Host Element

  /**
   * Get a reference to the host element
   */
  @Element() host: HTMLElement;

  // ================================================================ //
  // -- Native Attributes

  title: string;

  // ================================================================ //
  // -- Props

  /**
   * Text to be displayed in Modal header title.
   */
  @Prop({ mutable: true }) modalTitle: string;
  @Watch('modalTitle')
  onModalTitlehange(newValue: string, oldValue: string) {
    if (newValue !== oldValue) {
      this.setTitle();
    }
  }

  /**
   * What theme settings should the component use?
   * @type {string}
   */
  @Prop({ mutable: true }) overrideGlobalTheme: boolean;

  /**
   * If FluidThemeProvider is used, should this component override the
   * global theme with its local theme?
   */
  @Prop({ mutable: true }) theme: FluidTheme = FluidTheme.CORP;

  /**
   * Handles opening the modal
   */
  @Prop({ mutable: true }) open: boolean;

  /**
   * The message to show in the confirmation dialog
   */
  @Prop({ mutable: true }) message: string;

  /**
   * Custom label for the confirmation button
   */
  @Prop({ mutable: true }) confirmLabel = 'Confirm';
  @Watch('confirmLabel')
  onConfirmLabelChange(newValue: string, oldValue: string) {
    if (newValue !== oldValue) {
      this._modalActions = this.getActions();
    }
  }

  /**
   * Custom label for the cancel button
   */
  @Prop({ mutable: true }) cancelLabel = 'Cancel';
  @Watch('cancelLabel')
  onCancelLabelChange(newValue: string, oldValue: string) {
    if (newValue !== oldValue) {
      this._modalActions = this.getActions();
    }
  }

  // ================================================================ //
  // -- Events

  /**
   * Emitted when dialog is closed with confirmed response
   */
  @Event() confirmed: EventEmitter;

  /**
   * Emitted when dialog is closed with canceled response
   */
  @Event() cancelled: EventEmitter;

  // ================================================================ //
  // -- Lifecycle Hooks

  /**
   * Called every time the component is connected to the DOM.
   * @action: setGlobalTheme - updates theme to match global theme & subscribes to changes
   */
  connectedCallback(): void {
    Utils.setGlobalTheme(this);
    useI18n.bind(this)({
      language: () => (this._modalActions = this.getActions()),
    });
    this.title = this.host.getAttribute(ATTR.title);
    this._modalActions = this.getActions();
  }

  /**
   * Called every time the component is disconnected from the DOM.
   * @action deregisterElement - removes the element from the i18n Store registry
   */
  disconnectedCallback() {
    this._i18nStore?.deregisterElement(this._translateId);
  }

  /**
   * Called once just after the component is first connected to the DOM.
   */
  componentWillLoad(): void {
    this.setTitle();

    Utils.watchAttr(ATTR.title, this.host, () => {
      this.setTitle();
      // change detected then @Watch('title') will be triggered
    });
  }

  // ================================================================ //
  // -- Public Methods

  /**
   * Opens or closes the Dialog. If parameter is not provided, sets "open"
   * Prop to the inverse of its current value.
   * @param open - whether Dialog should be open (true) or closed (false)
   */
  @Method() async toggleDialog(open = !this.open) {
    this.open = !!open;
  }

  // ================================================================ //
  // -- Local Methods

  getActions() {
    return [
      {
        label:
          this._i18nStore?.translate(this.fluidTranslate?.confirmLabel) ||
          this.confirmLabel,
        closeModal: false,
        type: FluidButtonType.PRIMARY,
        eventKey: 'confirmed',
      } as FluidModalAction,
      {
        label:
          this._i18nStore?.translate(this.fluidTranslate?.cancelLabel) ||
          this.cancelLabel,
        closeModal: false,
        type: FluidButtonType.SECONDARY,
        eventKey: 'cancelled',
      } as FluidModalAction,
    ];
  }

  /**
   * Sets the local step title attribute from the native HTML attribute
   */
  setTitle(): void {
    this.title = this.modalTitle || this.host.getAttribute(ATTR.title);
  }

  /**
   * Handle the event emitted from the modal component
   * @param eventKey
   */
  handleModalEvent(eventKey: string) {
    eventKey === 'confirmed' ? this.confirmed.emit() : this.cancelled.emit();
    this.open = false;
  }

  // ================================================================ //
  // -- Render (Main)

  render() {
    return (
      <fluid-modal
        outsideClickClose={false}
        title={
          this._i18nStore?.translate(this.fluidTranslate?.modalTitle) ||
          this.title
        }
        size={FluidSize.SMALL}
        open={this.open}
        modalActions={this._modalActions}
        onModalClosed={(wasClosed: FluidModalCustomEvent<boolean>): void =>
          wasClosed ? this.handleModalEvent('cancelled') : undefined
        }
        onActionClicked={(action: FluidModalCustomEvent<string>) =>
          this.handleModalEvent(action.detail)
        }
        fluidTranslate={this.fluidTranslate}>
        <span
          innerHTML={
            this._i18nStore?.translate(this.fluidTranslate?.message) ||
            this.message
          }></span>
      </fluid-modal>
    );
  }

  // ================================================================ //
  // -- Internationalisation

  /* @internal */
  @Prop({ mutable: true })
  fluidTranslate: FluidConfirmationDialogTranslationOptions;
  private _translateId: string;
  private _i18nStore: FluidI18nStore | undefined;
}
